import { AccountManagementParams } from './AccountManagementParams';
import { PreSelectParams } from './PreSelectParams';
import { useStepContext, useDataPart } from '@/app/core/components/step-wizard';
import { ModuleInputProps } from '@/app/features/products/common/components/input-props';
import { useCallback, useEffect, useState } from 'react';
import { USAAParameters, USAAParamType } from '@ssar-app/domain';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { RadioGroup } from '@ssar-app/uikit';
import { Flex } from '@chakra-ui/react';

const paramValues = {
  runTypeOptions: ['prod', 'test-prod', 'prodqa', 'test-prodqa'],
  strategyTypeOptions: {
    AM: ['cli', 'cld', 'bt', 'scg', 'regz'],
    PS: ['cc', 'sc', 'cl', 'ita', 'ds'],
  },
  bureauCallIndOptions: [
    'P- Prod execution with client delivery',
    'T- Test with outgoing file to Ascend only. No client delivery',
    'X- Test with no outgoing file to Ascend. No client delivery',
  ],
};
export const ParametersSelection = (
  props: ModuleInputProps<{ productName: string }>,
) => {
  const { stepKey, validator } = props;
  const { productName: _productName } = props.moduleProps;

  const { isStepActive, onStepDeactivate } = useStepContext();
  const [dataPartState, setDataPartState] =
    useDataPart<USAAParameters>(stepKey);
  const [parametersInfo, setParametersInfo] = useState<Partial<USAAParameters>>(
    // dataPartState.data || {},
    {
      samplePercentage: '100',
      executeOnDemand: 'N',
      consolidatedInputFileDate: null,
      bureauFileDate: null,
      generateArtifacts: 'N',
      spcFlag: 'Y',
      ...(dataPartState.data || {}),
    },
  );

  const [isValidForm, setIsValidForm] = useState<boolean>(false);

  const formMethods = useForm<USAAParameters>({
    defaultValues: parametersInfo,
    mode: 'onChange',
    context: {},
    resolver: yupResolver(validator, {
      abortEarly: false,
    }),
  });
  const { control, formState, trigger } = formMethods;

  onStepDeactivate(() => {
    return new Promise((_resolve, _reject) => {
      trigger().finally(() => {
        updateState();
      });
      _resolve(true);
    });
  });

  const updateState = useCallback(() => {
    setDataPartState((currentState) => {
      const newState = {
        ...currentState,
        isValid: isValidForm,
        isDirty: formState.isDirty,
        data: {
          ...currentState.data,
          ...parametersInfo,
        },
      };
      return newState;
    });
  }, [formState, parametersInfo, setDataPartState, isValidForm]);

  useEffect(() => {
    if (parametersInfo && validator) {
      validator
        .validate(parametersInfo)
        .then(() => {
          setIsValidForm(true);
        })
        .catch((_error) => {
          setIsValidForm(false);
        });
    }
  }, [parametersInfo, validator]);

  useEffect(() => {
    updateState();
  }, [isValidForm, updateState]);

  useEffect(() => {
    if (parametersInfo) {
      updateState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametersInfo]);

  if (!isStepActive) {
    return null;
  }
  const getParamsComponent = () => {
    switch (parametersInfo.type) {
      case USAAParamType.AccountManagement:
        return (
          <AccountManagementParams
            formMethods={formMethods}
            model={{
              BureauCallIndicator: null,
              SerJsonValidate: null,
              ...parametersInfo,
            }}
            paramOptions={{
              runTypeOptions: paramValues.runTypeOptions,
              strategyTypeOptions: paramValues.strategyTypeOptions.AM,
              BureauCallIndOptions: paramValues.bureauCallIndOptions,
            }}
            setModel={setParametersInfo}
          />
        );
      case USAAParamType.PreSelect:
        return (
          <PreSelectParams
            formMethods={formMethods}
            model={{
              marketSwitchFlag: null,
              ...parametersInfo,
            }}
            paramOptions={{
              runTypeOptions: paramValues.runTypeOptions,
              strategyTypeOptions: paramValues.strategyTypeOptions.PS,
              BureauCallIndOptions: [],
            }}
            setModel={setParametersInfo}
          />
        );
    }
  };

  return (
    <Flex direction='column' w='full'>
      <Controller
        name='type'
        control={control}
        defaultValue={parametersInfo.type}
        render={({ onChange, value }) => {
          return (
            <RadioGroup
              onChange={(val: USAAParamType) => {
                onChange(val);
                setParametersInfo(
                  ({
                    runType,
                    strategy,
                    marketSwitchFlag,
                    BureauCallIndicator,
                    SerJsonValidate,
                    ...paramsInfo
                  }) => ({
                    ...(val === USAAParamType.PreSelect
                      ? {
                          marketSwitchFlag: marketSwitchFlag || 'N',
                          runType: runType || paramValues.runTypeOptions[0],
                          strategy: paramValues.strategyTypeOptions.PS[0],
                        }
                      : {
                          BureauCallIndicator:
                            BureauCallIndicator ||
                            paramValues.bureauCallIndOptions[1],
                          SerJsonValidate: SerJsonValidate || 'N',
                          runType: runType || paramValues.runTypeOptions[0],
                          strategy: paramValues.strategyTypeOptions.AM[0],
                        }),
                    ...paramsInfo,
                    type: val,
                  }),
                );
              }}
              optionsType='button'
              value={value}
              options={Object.entries(USAAParamType).map(([name, code]) => ({
                label: name,
                value: code,
              }))}
              ml={4}
              my={3}
              applyBorderRadiusForStartEndButtons={true}
              borderRadiusForStartEndButtons={5}
            />
          );
        }}
      />
      {parametersInfo.type && getParamsComponent()}
    </Flex>
  );
};
